import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useTracking } from '@/hooks/useTracking';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export interface VoteTicketRequest {
  voteType: 'upvote' | 'downvote' | 'remove';
}

export interface VoteTicketResponse {
  message: string;
  voteCount: number;
  upvotes: string[];
  downvotes: string[];
  userVote: 'upvote' | 'downvote' | null;
}

function extractWorkspaceId(workspaceObj: unknown): string {
  const w = workspaceObj as { id?: string; SK?: string } | null | undefined;
  if (!w) return '';
  if (w.id) return w.id;
  if (w.SK) {
    if (w.SK.startsWith('WS#')) return w.SK.substring(3);
    return w.SK;
  }
  return '';
}

function currentUserIdForVotes(user: unknown): string {
  const payload = (user as { signInUserSession?: { idToken?: { payload?: Record<string, unknown> } } } | null | undefined)
    ?.signInUserSession?.idToken?.payload;
  const sub = payload?.sub;
  const username = payload?.['cognito:username'];
  return (
    (typeof sub === 'string' ? sub : '') ||
    (typeof username === 'string' ? username : '')
  ).trim();
}

async function voteTicket(
  ticketId: string,
  data: VoteTicketRequest,
  sessionJwt: string | undefined,
  workspaceId: string,
): Promise<VoteTicketResponse> {
  const bearer = resolveBearerToken(sessionJwt);
  const headers = backendRequestHeaders(bearer || undefined);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const response = await axios.put<VoteTicketResponse>(
    `${getBackendBaseUrl()}/workspaces/${workspaceId}/tickets/${ticketId}/vote`,
    { voteType: data.voteType },
    { headers },
  );

  return response.data;
}

export function useVoteTicket() {
  const { user } = useAuth();
  const { trackEvent } = useTracking();
  const queryClient = useQueryClient();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaceId = extractWorkspaceId(workspace);

  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: VoteTicketRequest }) => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      return voteTicket(ticketId, data, user?.signInUserSession?.idToken?.jwtToken, workspaceId);
    },
    onMutate: async ({ ticketId, data }) => {
      await queryClient.cancelQueries([queryKeys.tickets, workspaceId]);

      const previousTickets = queryClient.getQueryData([queryKeys.tickets, workspaceId]);

      const userId = currentUserIdForVotes(user);

      queryClient.setQueryData([queryKeys.tickets, workspaceId], (old: any) => {
        if (!old) return old;

        const newTickets = old.tickets.map((t: any) => {
          if (t.ticketId === ticketId || t.id === ticketId) {
            const upvotes = [...(t.upvotes || [])];
            const downvotes = [...(t.downvotes || [])];

            const upvoteIndex = upvotes.indexOf(userId);
            const downvoteIndex = downvotes.indexOf(userId);
            if (upvoteIndex > -1) upvotes.splice(upvoteIndex, 1);
            if (downvoteIndex > -1) downvotes.splice(downvoteIndex, 1);

            if (data.voteType === 'upvote') {
              upvotes.push(userId);
            } else if (data.voteType === 'downvote') {
              downvotes.push(userId);
            }

            const voteCount = upvotes.length - downvotes.length;

            return { ...t, upvotes, downvotes, voteCount };
          }
          return t;
        });

        return { ...old, tickets: newTickets };
      });

      return { previousTickets };
    },
    onError: (err: any, _variables, context: any) => {
      if (context?.previousTickets) {
        queryClient.setQueryData([queryKeys.tickets, workspaceId], context.previousTickets);
      }
      console.error('Vote failed:', err);
    },
    onSuccess: (res, { ticketId, data: votePayload }) => {
      trackEvent('ticket_voted', {
        ticket_id: ticketId,
        vote_type: votePayload.voteType,
        vote_count: res.voteCount,
      });
      queryClient.setQueryData([queryKeys.tickets, workspaceId], (old: any) => {
        if (!old) return old;
        const newTickets = old.tickets.map((t: any) => {
          if (t.ticketId === ticketId || t.id === ticketId) {
            return {
              ...t,
              upvotes: res.upvotes,
              downvotes: res.downvotes,
              voteCount: res.voteCount,
            };
          }
          return t;
        });
        return { ...old, tickets: newTickets };
      });
    },
  });
}
